import React from 'react';
import {
  Grid,
  TextField,
  Divider,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';
import UIContainer from '../UI/Container/UIContainer';
import { usePOS } from '../../contexts/POSContext';
import { formattedCurrency, normalizeText } from '../../utils/utils';

const POSSummaryPanel = ({
  onOpenCustomer,
  onOpenCoupon,
  onOpenDiscount,
  onOpenPlots,
  onOpenNote,
  onOpenShortcuts,
}) => {
  const {
    totals = {},
    customers,
    selectedCustomer,
    setSelectedCustomer,
    promotions,
    appliedPromotion,
    setAppliedPromotion,
    manualDiscount,
    setManualDiscount,
    plots,
    setPlots,
    interest,
    setInterest,
    observation,
    isDarkMode,
  } = usePOS();

  const handleCustomerChange = (event, newValue) => {
    setSelectedCustomer(newValue);
  };

  const handlePromotionChange = (e) => {
    const promoId = e.target.value;
    if (promoId === 1) {
      setAppliedPromotion(null);
    } else {
      const found = promotions.find((p) => p.id === promoId);
      if (found) setAppliedPromotion(found);
    }
  };

  const currentPlots = Math.max(1, parseInt(plots, 10) || 1);
  const plotValue = (totals.total || 0) / currentPlots;

  return (
    <UIContainer
      container
      direction="column"
      style={{
        width: '100%',
        height: '100%',
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        overflowY: 'auto',
      }}
    >
      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#17497F' }}>
        Opções da Venda
      </span>

      {/* Cliente */}
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Autocomplete
            options={customers || []}
            getOptionSelected={(option, value) => option.id === value?.id}
            getOptionLabel={(option) => {
              if (!option) return '';
              const fullName = `${option.name || ''} ${option.lastName || option.last_name || ''}`.trim();
              return `${fullName || option.name} ${option.cpf ? `(${option.cpf})` : ''}`.trim();
            }}
            filterOptions={(optionsList, state) => {
              const term = normalizeText(state.inputValue);
              if (!term) return optionsList;
              return optionsList.filter((option) => {
                const fullName = normalizeText(`${option.name || ''} ${option.lastName || option.last_name || ''}`);
                const cpf = normalizeText(option.cpf);
                const phone = normalizeText(option.phone || option.whatsapp);
                const id = String(option.id);
                return fullName.includes(term) || cpf.includes(term) || phone.includes(term) || id.includes(term);
              });
            }}
            renderOption={(option) => {
              const fullName = `${option.name || ''} ${option.lastName || option.last_name || ''}`.trim();
              return (
                <div style={{ display: 'flex', flexDirection: 'column', padding: '2px 0' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>
                    {fullName || option.name}
                  </span>
                  {(option.cpf || option.phone || option.whatsapp) && (
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      {[option.cpf ? `CPF: ${option.cpf}` : null, option.phone || option.whatsapp].filter(Boolean).join(' • ')}
                    </span>
                  )}
                </div>
              );
            }}
            value={selectedCustomer}
            onChange={handleCustomerChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Cliente (F7)"
                placeholder="Nome, sobrenome, CPF..."
                size="small"
                fullWidth
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Promoção / Convênio */}
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Promoção / Cupom (F8)</InputLabel>
            <Select
              value={appliedPromotion ? appliedPromotion.id : 1}
              onChange={handlePromotionChange}
              label="Promoção / Cupom (F8)"
              MenuProps={{
                PaperProps: {
                  className: isDarkMode ? 'pos-dark-paper' : '',
                },
              }}
            >
              <MenuItem value={1}>Nenhuma / Padrão</MenuItem>
              {promotions.filter((p) => p.id !== 1).map((promo) => (
                <MenuItem key={promo.id} value={promo.id}>
                  {promo.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Desconto */}
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <CurrencyTextField
            type="tel"
            label="Desconto na Venda (F9)"
            decimalCharacter=","
            digitGroupSeparator="."
            currencySymbol="R$"
            outputFormat="number"
            size="small"
            value={totals.manualDiscount}
            onChange={(ev, value) => {
              setManualDiscount(value || 0);
            }}
            fullWidth
            disabled={Boolean(appliedPromotion && appliedPromotion.id !== 1)}
          />
        </Grid>
      </Grid>

      {/* Parcelas */}
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <TextField
            label="Parcelamento (Qtd)"
            type="number"
            size="small"
            value={plots}
            onChange={(e) => {
              const val = Math.max(1, Math.min(24, parseInt(e.target.value, 10) || 1));
              setPlots(val);
            }}
            inputProps={{ min: 1, max: 24 }}
            fullWidth
          />
        </Grid>
      </Grid>

      <Divider style={{ margin: '6px 0' }} />

      {/* Tabela de Info do Pedido */}
      <TableContainer style={{ flex: 1 }}>
        <Typography align="center" variant="subtitle2" style={{ color: '#616161', fontWeight: 700, marginBottom: 4 }}>
          Info do Pedido
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight: 600, color: '#757575', padding: '4px 0' }}>Cliente</TableCell>
              <TableCell align="right" style={{ fontWeight: 600, color: '#212121', padding: '4px 0' }}>
                {selectedCustomer ? `${selectedCustomer.name} ${selectedCustomer.lastName || ''}`.trim() : 'Avulso'}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ fontWeight: 600, color: '#757575', padding: '4px 0' }}>Pagamento</TableCell>
              <TableCell align="right" style={{ fontWeight: 600, color: '#212121', padding: '4px 0' }}>
                {currentPlots > 1 ? `Em ${currentPlots}x de ${formattedCurrency(plotValue)}` : 'À Vista'}
              </TableCell>
            </TableRow>
            {totals.totalDiscount > 0 && (
              <TableRow>
                <TableCell style={{ fontWeight: 600, color: '#2e7d32', padding: '4px 0' }}>Descontos</TableCell>
                <TableCell align="right" style={{ fontWeight: 700, color: '#2e7d32', padding: '4px 0' }}>
                  - {formattedCurrency(totals.totalDiscount)}
                </TableCell>
              </TableRow>
            )}
          </TableHead>
        </Table>
      </TableContainer>
    </UIContainer>
  );
};

export default POSSummaryPanel;
